import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
// Quitamos EMAILJS_TEMPLATE_ID de aquí para no usar el de visitas por error
import { db, EMAILJS_SERVICE_ID, EMAILJS_PUBLIC_KEY } from '../config/firebase';
import { useAuth } from '../context/AuthContext';
import emailjs from 'emailjs-com';
import { PaperAirplaneIcon, InboxArrowDownIcon, ArrowTopRightOnSquareIcon, ClockIcon, UserCircleIcon } from '@heroicons/react/24/solid';

const MailPage = ({ setPage, setNotification }) => {
    const { user } = useAuth();
    const [activeTab, setActiveTab] = useState('compose');
    const [loading, setLoading] = useState(false);
    
    // --- CONFIGURACIÓN ESPECÍFICA PARA CORREOS GENERALES ---
    // PEGA AQUÍ EL ID DE TU NUEVA PLANTILLA SIMPLE DE EMAILJS (La que solo tiene {{message}})
    const GENERIC_EMAIL_TEMPLATE_ID = "template_7wxilhu"; // <--- ¡REEMPLAZA ESTO CON TU NUEVO ID!

    // Estados del formulario
    const [to, setTo] = useState('');
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [cc, setCc] = useState('');

    // Estado del historial
    const [emailLogs, setEmailLogs] = useState([]);

    // Cargar historial
    useEffect(() => {
        if (activeTab === 'history') {
            const fetchLogs = async () => {
                setLoading(true);
                try {
                    const q = query(collection(db, 'email_logs'), orderBy('sentAt', 'desc'));
                    const snapshot = await getDocs(q);
                    setEmailLogs(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                } catch (error) {
                    console.error("Error cargando logs:", error);
                } finally {
                    setLoading(false);
                }
            };
            fetchLogs();
        }
    }, [activeTab]);

    const handleSend = async (e) => {
        e.preventDefault();
        setLoading(true);

        // 1. Validación
        if (!EMAILJS_SERVICE_ID || !EMAILJS_PUBLIC_KEY) {
            setNotification({ message: 'Faltan claves de EmailJS en config.', type: 'error' });
            setLoading(false);
            return;
        }

        if (GENERIC_EMAIL_TEMPLATE_ID === "COLOCA_AQUI_TU_NUEVO_ID") {
             setNotification({ message: 'Falta configurar el ID de la plantilla nueva en el código (MailPage.js).', type: 'warning' });
             setLoading(false);
             return;
        }

        const templateParams = {
            to_email: to,
            cc_email: cc,
            subject: subject,
            message: message, // Esta variable debe existir en tu nueva plantilla
            from_name: user.displayName || user.email,
            reply_to: user.email
        };

        try {
            // 2. Enviar usando la PLANTILLA GENÉRICA
            await emailjs.send(EMAILJS_SERVICE_ID, GENERIC_EMAIL_TEMPLATE_ID, templateParams, EMAILJS_PUBLIC_KEY);

            setNotification({ message: `Correo enviado correctamente.`, type: 'success' });
            setTo('');
            setSubject('');
            setMessage('');
            setCc('');

            // 3. Guardar historial
            try {
                await addDoc(collection(db, 'email_logs'), {
                    to,
                    cc,
                    subject,
                    bodySnippet: message.substring(0, 100) + '...',
                    sentBy: user.email,
                    sentAt: serverTimestamp(),
                    type: 'Manual'
                });
            } catch (dbError) {
                console.warn("No se pudo guardar en historial:", dbError);
            }

        } catch (error) {
            console.error('Error EmailJS:', error);
            let errorMessage = 'Error desconocido al enviar.';
            if (error.text) errorMessage = `EmailJS: ${error.text}`;
            setNotification({ message: errorMessage, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const openWebmail = () => {
        window.open('https://webmail.bant.cl', '_blank'); 
    };

    return (
        <div className="p-4 md:p-6 bg-gray-50 dark:bg-gray-900 min-h-screen transition-colors">
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div className="flex items-center">
                    <button onClick={() => setPage('hub')} className="text-blue-600 dark:text-blue-400 hover:underline font-semibold mr-4 transition-colors">&larr; Volver</button>
                    <h1 className="text-3xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Gestor de Correos</h1>
                </div>
                <button 
                    onClick={openWebmail}
                    className="bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800/50 px-4 py-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/30 flex items-center shadow-sm transition justify-center"
                    title="Abrir bandeja de entrada en proveedor"
                >
                    <InboxArrowDownIcon className="w-5 h-5 mr-2"/>
                    Ir a Bandeja de Entrada
                    <ArrowTopRightOnSquareIcon className="w-4 h-4 ml-1"/>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                {/* Sidebar */}
                <div className="lg:col-span-1 space-y-2">
                    <button 
                        onClick={() => setActiveTab('compose')}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition ${activeTab === 'compose' ? 'bg-blue-600 dark:bg-blue-700 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                        <PaperAirplaneIcon className="w-5 h-5 mr-3"/> Redactar Nuevo
                    </button>
                    <button 
                        onClick={() => setActiveTab('history')}
                        className={`w-full text-left px-4 py-3 rounded-lg flex items-center font-medium transition ${activeTab === 'history' ? 'bg-blue-600 dark:bg-blue-700 text-white shadow-md' : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                    >
                        <ClockIcon className="w-5 h-5 mr-3"/> Historial de Enviados
                    </button>
                </div>

                {/* Área Principal */}
                <div className="lg:col-span-3">
                    {activeTab === 'compose' && (
                        <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 transition-colors">
                            <div className="flex justify-between items-center border-b dark:border-gray-700 pb-4 mb-6 transition-colors">
                                <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Redactar Correo</h2>
                                <div className="flex items-center text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 px-3 py-1 rounded-full transition-colors">
                                    <UserCircleIcon className="w-4 h-4 mr-2 text-blue-500 dark:text-blue-400"/>
                                    Enviando como: <span className="font-semibold ml-1">{user.email}</span>
                                </div>
                            </div>

                            <form onSubmit={handleSend} className="space-y-4">
                                {/* Aviso de Configuración */}
                                {GENERIC_EMAIL_TEMPLATE_ID === "COLOCA_AQUI_TU_NUEVO_ID" && (
                                    <div className="bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 dark:border-yellow-700 p-4 mb-4 transition-colors">
                                        <p className="text-sm text-yellow-700 dark:text-yellow-400 transition-colors">
                                            <strong>Atención:</strong> Debes crear una nueva plantilla en EmailJS (simple, solo con la variable <code>{`{{message}}`}</code>) y pegar su ID en el código de <code>MailPage.js</code> para que esto funcione.
                                        </p>
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">Para</label>
                                        <input type="email" value={to} onChange={e => setTo(e.target.value)} className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 transition-colors" required placeholder="cliente@ejemplo.com"/>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">CC (Copia)</label>
                                        <input type="email" value={cc} onChange={e => setCc(e.target.value)} className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 transition-colors" placeholder="copia@bant.cl"/>
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">Asunto</label>
                                    <input type="text" value={subject} onChange={e => setSubject(e.target.value)} className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 transition-colors" required placeholder="Asunto del mensaje"/>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 transition-colors">Mensaje</label>
                                    <textarea 
                                        value={message} 
                                        onChange={e => setMessage(e.target.value)} 
                                        rows="8" 
                                        className="w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-blue-500 dark:focus:ring-blue-400 focus:border-blue-500 transition-colors" 
                                        required 
                                        placeholder="Escribe tu mensaje aquí..."
                                    ></textarea>
                                </div>
                                <div className="flex justify-end pt-2">
                                    <button type="submit" disabled={loading} className="bg-blue-600 dark:bg-blue-700 text-white px-8 py-2 rounded-lg hover:bg-blue-700 dark:hover:bg-blue-600 font-bold shadow-md disabled:bg-blue-300 dark:disabled:bg-blue-900/50 flex items-center transition-colors">
                                        {loading ? 'Enviando...' : <><PaperAirplaneIcon className="w-5 h-5 mr-2"/> Enviar Correo</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    )}

                    {activeTab === 'history' && (
                        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden transition-colors">
                            <div className="p-4 border-b dark:border-gray-700 bg-gray-50 dark:bg-gray-800 transition-colors">
                                <h2 className="text-lg font-bold text-gray-800 dark:text-gray-100 transition-colors">Historial de Comunicaciones</h2>
                            </div>
                            {loading ? (
                                <div className="p-8 text-center text-gray-500 dark:text-gray-400 transition-colors">Cargando historial...</div>
                            ) : emailLogs.length === 0 ? (
                                <div className="p-8 text-center text-gray-400 dark:text-gray-500 italic transition-colors">No se han enviado correos desde el sistema aún.</div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                                        <thead className="bg-gray-50 dark:bg-gray-700/50 transition-colors">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Fecha</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Destinatario</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Asunto</th>
                                                <th className="px-6 py-3 text-left text-xs font-bold text-gray-500 dark:text-gray-400 uppercase transition-colors">Enviado Por</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700 transition-colors">
                                            {emailLogs.map((log) => (
                                                <tr key={log.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 transition-colors">
                                                        {log.sentAt ? new Date(log.sentAt.seconds * 1000).toLocaleString('es-CL') : '-'}
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-100 transition-colors">
                                                        {log.to}
                                                    </td>
                                                    <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-200 transition-colors">
                                                        {log.subject || '(Sin asunto)'}
                                                        <span className="block text-xs text-gray-400 dark:text-gray-500 mt-1 truncate max-w-xs transition-colors">{log.bodySnippet}</span>
                                                    </td>
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400 transition-colors">
                                                        {log.sentBy}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MailPage;